import React, { useState } from "react";
import { toast } from "sonner";
import { Share2, Copy, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useLang } from "@/context/LanguageContext";

// Simple SVGs for share targets (avoid heavy icon deps)
const twitterIcon = "𝕏";

export default function ShareButton({ variant = "primary", size = "md", url, title }) {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== "undefined" ? window.location.origin : "https://sunbraids.example");
  const message = title || t("share.message");
  const text = `${message} ${shareUrl}`;

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Reiki for Ukraine — Sunbraider", text: message, url: shareUrl });
      } catch { /* user cancelled */ }
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t("share.copied"));
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const targets = [
    { label: "Telegram", href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(message)}` },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { label: "X (Twitter)", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}` },
    { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
    { label: "Email", href: `mailto:?subject=${encodeURIComponent("Reiki for Ukraine — Sunbraider")}&body=${encodeURIComponent(text)}` },
  ];

  const btnClass =
    variant === "primary"
      ? "btn-primary"
      : variant === "ghost"
      ? "btn-ghost-link"
      : "btn-secondary";
  const sizeMod = size === "sm" ? "!py-2 !text-[11px]" : "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="share-mission-trigger" className={`${btnClass} ${sizeMod}`}>
        <Share2 className="h-4 w-4" strokeWidth={1.5} /> {t("share.label")}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[14rem] rounded-xl bg-ivory border border-forest-900/10">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-forest-700/70">
          {t("share.label")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {typeof navigator !== "undefined" && navigator.share && (
          <DropdownMenuItem onSelect={nativeShare} data-testid="share-native" className="cursor-pointer">
            <Share2 className="mr-2 h-4 w-4" strokeWidth={1.5} /> Native share…
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={copy} data-testid="share-copy" className="cursor-pointer">
          {copied ? <Check className="mr-2 h-4 w-4 text-wheat-700" strokeWidth={1.5} /> : <Copy className="mr-2 h-4 w-4" strokeWidth={1.5} />}
          {copied ? t("share.copied") : t("share.copy")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {targets.map((tg) => (
          <DropdownMenuItem key={tg.label} asChild data-testid={`share-${tg.label.toLowerCase().split(" ")[0]}`}>
            <a href={tg.href} target="_blank" rel="noreferrer" className="cursor-pointer">
              {tg.label === "X (Twitter)" && <span className="mr-2 text-sm font-semibold">{twitterIcon}</span>}
              {tg.label}
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
